import React from "react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import {
  InternalLink,
  LocationLink,
  AirportLink,
  RouteLink,
  ServiceLink,
} from "@/components/InternalLink";
import { ICOBadge } from "@/components/ICOBadge";

interface EnhancedFooterProps {
  className?: string;
  showExtendedLinks?: boolean;
}

export function EnhancedFooter({
  className = "",
  showExtendedLinks = false,
}: EnhancedFooterProps) {
  return (
    <footer className={`bg-gray-900 text-white py-12 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6 md:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-semibold mb-4">Tigola</h3>
            <p className="text-gray-400 mb-4">
              Reliable transportation across the UK, connecting you to airports
              and cities nationwide.
            </p>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <a href="/about" className="hover:text-white transition-colors">
                  About us
                </a>
              </li>
              <li>
                <a
                  href="/careers"
                  className="hover:text-white transition-colors"
                >
                  Careers
                </a>
              </li>
              <li>
                <a href="/press" className="hover:text-white transition-colors">
                  Press
                </a>
              </li>
              <li>
                <a href="/blog" className="hover:text-white transition-colors">
                  Blog
                </a>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-semibold mb-4">Products</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <a href="/" className="hover:text-white transition-colors">
                  Ride
                </a>
              </li>
              <li>
                <a href="/drive" className="hover:text-white transition-colors">
                  Drive
                </a>
              </li>
              <li>
                <a
                  href="/business"
                  className="hover:text-white transition-colors"
                >
                  Business
                </a>
              </li>
            </ul>
          </div>

          {/* Travel */}
          <div>
            <h4 className="font-semibold mb-4">Travel</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <a
                  href="/reserve"
                  className="hover:text-white transition-colors"
                >
                  Reserve
                </a>
              </li>
              <li>
                <a
                  href="/airports"
                  className="hover:text-white transition-colors"
                >
                  Airport transfers
                </a>
              </li>
              <li>
                <a href="/taxi" className="hover:text-white transition-colors">
                  Cities
                </a>
              </li>
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-semibold mb-4">Quick links</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <a
                  href="/book-online"
                  title="Book a taxi or minicab online"
                  className="hover:text-white transition-colors"
                >
                  Book Online
                </a>
              </li>
              <li>
                <a
                  href="/app?src=website"
                  title="Download the Tigola rider app"
                  className="hover:text-white transition-colors"
                >
                  Download Rider App
                </a>
              </li>
              <li>
                <a
                  href="/help"
                  title="Frequently asked questions and support"
                  className="hover:text-white transition-colors"
                >
                  FAQ
                </a>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <a href="/help" className="hover:text-white transition-colors">
                  Help center
                </a>
              </li>
              <li>
                <a
                  href="/safety"
                  className="hover:text-white transition-colors"
                >
                  Safety
                </a>
              </li>
              <li>
                <a
                  href="/contact"
                  className="hover:text-white transition-colors"
                >
                  Contact us
                </a>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="hover:text-white transition-colors"
                >
                  Terms
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="hover:text-white transition-colors"
                >
                  Privacy
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 flex-wrap">
            <div className="text-center md:text-left order-2 md:order-1">
              <p className="text-gray-400 text-sm">
                © 2025 Tigola. All rights reserved.
              </p>
            </div>

            {/* ICO Badge - Trust & Transparency */}
            <div className="order-1 md:order-2">
              <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-4 py-2">
                <ICOBadge size="small" layout="horizontal" showText={true} />
              </div>
            </div>

            <ul className="flex items-center gap-4 text-sm text-gray-400 mx-auto md:mx-0 order-3">
              <li>
                <a href="/terms" className="hover:text-white transition-colors">
                  Terms
                </a>
              </li>
              <li className="text-gray-600">|</li>
              <li>
                <a
                  href="/privacy"
                  className="hover:text-white transition-colors"
                >
                  Privacy
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default EnhancedFooter;
